import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'app/core/services/config.service';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

/**
 * Result of resolving a tenant URL to its intended Auth0 connection.
 */
export interface TenantSsoInfo {
  /** Auth0 connection name (e.g. "fortinet-sso"), or null if not a tenant SSO route. */
  connection: string | null;
  /** True when the tenant wants a login chooser (SSO primary, password fallback). */
  preferSso: boolean;
  /** True when the tenant has disabled password auth and must use SSO only. */
  ssoOnly: boolean;
  /** True when the tenant has an enabled self-hosted auth provider → use /auth/login. */
  selfHosted: boolean;
  /** Self-hosted provider type ('local' | 'saml' | 'oidc') selected by the backend. */
  provider?: string | null;
  /** SP login endpoint for SAML providers — redirect there instead of /auth/login. */
  initiateUrl?: string | null;
  /** False when the tenant disabled username/password (sso-only) or no local provider is enabled. */
  passwordEnabled?: boolean;
}

/** Legacy REST response shape from /api/auth/sso-lookup. */
interface SsoLookupResponse {
  matched: boolean;
  auth0ConnectionName: string | null;
  preferSso?: boolean;
  ssoOnly?: boolean;
  selfHosted?: boolean;
  provider?: string;
  initiateUrl?: string;
  passwordEnabled?: boolean;
}

/**
 * Injectable resolver for the Auth0 connection that should be used for the
 * current tenant URL. Centralizes the SSO lookup so every frontend redirect
 * to Auth0 can force the correct connection and avoid leaking the password
 * connection to SSO-only tenants.
 */
@Injectable({ providedIn: 'root' })
export class TenantSsoResolver {
  // HttpClient is resolved lazily inside lookup() — injecting it in the
  // constructor creates a DI cycle when this service is first built inside
  // the HTTP_INTERCEPTORS chain (AuthTokenInterceptor ->
  // AuthInitializerService -> TenantSsoResolver -> HttpClient -> in-flight
  // HTTP_INTERCEPTORS), which produced NG0200 on cold-boot tenant origins.
  constructor(
    private injector: Injector,
    private configService: ConfigService
  ) {}

  /**
   * Look up the SSO connection for a URL pattern. Falls back to a safe default
   * (no connection enforced) if the API call fails or returns no match.
   */
  lookup(urlPattern: string): Observable<TenantSsoInfo> {
    return this.injector.get(HttpClient).post<SsoLookupResponse>('/api/auth/sso-lookup', { urlPattern }).pipe(
      timeout(5000),
      map(response => this.normalizeResponse(response)),
      catchError(error => {
        console.warn('TenantSsoResolver: lookup failed, falling back to no connection', error);
        return of(this.fallbackFromUrl(urlPattern));
      })
    );
  }

  /**
   * Synchronous fallback for callers that cannot wait for the HTTP lookup.
   *
   * Does NOT derive a connection from the hostname; that caused the frontend
   * to force non-existent `<subdomain>-sso` connections for tenants that only
   * have password auth or a differently-named SSO connection. The backend is
   * the authoritative source for whether a tenant enforces SSO.
   */
  fallbackFromUrl(_urlPattern: string): TenantSsoInfo {
    return { connection: null, preferSso: false, ssoOnly: false, selfHosted: false, provider: null, initiateUrl: null };
  }

  private normalizeResponse(response: SsoLookupResponse): TenantSsoInfo {
    const connection = response.matched ? response.auth0ConnectionName : null;

    if (connection || response.selfHosted) {
      return {
        connection,
        preferSso: !!response.preferSso,
        ssoOnly: !!response.ssoOnly,
        selfHosted: !!response.selfHosted,
        provider: response.provider ?? null,
        initiateUrl: response.initiateUrl ?? null,
        passwordEnabled: response.passwordEnabled
      };
    }

    return { connection: null, preferSso: false, ssoOnly: false, selfHosted: false, provider: null, initiateUrl: null };
  }
}
